import React from 'react';
import './NuiteePrivacyContent.css';

/**
 * Full Nuitee Privacy Policy content, including the data-processing table.
 * Shared so any modal on any booking step always renders the same content.
 */
const NuiteePrivacyContent: React.FC = () => (
  <>
    <h4>Who We Are</h4>
    <p>Nuitée Travel Limited is a company incorporated and registered in Ireland. Our registered office is located at 4 Waterloo Rd, Ballsbridge, Dublin, D04 A0X3, Ireland</p>

    <h4>What This Policy Covers</h4>
    <p>This Privacy Policy describes how Nuitée Travel Limited collects, uses, processes, and shares personal information. This policy applies to all users of our website and our services.</p>

    <h4>Information We Collect</h4>
    <p>When you use our website or services, we may collect the following types of information:</p>
    <ul className="nuitee-privacy-list">
      <li><strong>Personal Identification Information:</strong> Name, email address, phone number, etc.</li>
      <li><strong>Technical Data:</strong> IP address, browser type and version, time zone setting, location, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access this website.</li>
      <li><strong>Usage Data:</strong> Information about how you use our website, products, and services.</li>
      <li><strong>Marketing and Communications Data:</strong> Your preferences in receiving marketing from us and your communication preferences.</li>
    </ul>
    <p>We collect this data under the lawful bases set out in the GDPR, which include your consent, contract performance, legitimate business interests, and compliance with our legal obligations.</p>

    <h4>How We Use Your Information</h4>
    <p>We use the information we collect for various purposes, including:</p>
    <ul className="nuitee-privacy-list">
      <li>To provide our services to you, in accordance with our Terms and Conditions.</li>
      <li>To maintain and improve our website and services, in pursuit of our legitimate interests to provide an engaging and relevant experience to our users.</li>
      <li>To communicate with you, including to respond to your comments or inquiries, in accordance with our contract performance duties and legitimate business interests.</li>
      <li>To conduct marketing and promotions, based on your consent or our legitimate business interests.</li>
    </ul>

    <h4>Sharing Your Information</h4>
    <p>We may share your personal data with third-party service providers who perform functions on our behalf, including web hosting, data analysis, payment processing, order fulfillment, customer service, and marketing assistance. We ensure that our third-party service providers respect your personal data and comply with GDPR and other relevant data protection laws.</p>

    <h4>Your Rights</h4>
    <p>Under the GDPR, you have rights in relation to your personal data, including the right to access, correct, update, or request deletion of your personal data. You can also object to the processing of your personal data, ask us to restrict processing of your personal data, or request portability of your personal data. If we have collected and processed your personal data with your consent, you can withdraw your consent at any time.</p>
    <p>To exercise any of these rights, please contact us at <a href="mailto:contact@nuitee.com">contact@nuitee.com</a>. We will respond to your request within a reasonable timeframe and in any event within any timeframe required by law.</p>

    <h4>Cookies</h4>
    <p>We use cookies and similar tracking technologies to track the activity on our website and hold certain information.</p>

    <h4>Data Retention</h4>
    <p>We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.</p>

    <h4>How We Use Your Personal Data</h4>
    <p>We utilize your personal data for a variety of purposes, which are identified and described in the table below. Along with the purpose, we also specify the types of data we collect and the legal basis on which we process them.</p>
    <div className="nuitee-privacy-table-wrapper">
      <table className="nuitee-privacy-table">
        <thead>
          <tr>
            <th>Purpose for Processing</th>
            <th>Types of Data Collected</th>
            <th>Legal Basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Registering you as a new customer and completing our onboarding and KYC procedures.</td>
            <td>Identity, Contact, Financial &amp; Governmental Data.</td>
            <td>Fulfilling our service agreement, Legitimate interest (maintaining updated records).</td>
          </tr>
          <tr>
            <td>Processing and delivering your booking which includes managing payments, fees, charges, recovering debts, and returning funds in case of cancellations, refunds or price adjustments.</td>
            <td>Identity, Contact, Financial, Transaction, Marketing Communications.</td>
            <td>Fulfilling our service agreement, Legitimate interest (debt recovery).</td>
          </tr>
          <tr>
            <td>Managing our relationship with you, which includes notifying you about changes in our terms or privacy policy, inviting you to review our services or participate in surveys, and soliciting feedback on new features.</td>
            <td>Identity, Contact, Profile, Marketing Communications.</td>
            <td>Fulfilling our service agreement, Legal obligations, Legitimate interest (maintaining updated records and understanding customer service usage).</td>
          </tr>
          <tr>
            <td>Administering and protecting our business and website, which includes troubleshooting, data analysis, testing, system maintenance, support, reporting, and hosting of data.</td>
            <td>Identity, Contact, Technical.</td>
            <td>Legitimate interest (business operations, IT services, network security, fraud prevention, business reorganization), Legal obligations.</td>
          </tr>
          <tr>
            <td>Delivering relevant website content and advertisements to you, and understanding the effectiveness of our advertising.</td>
            <td>Identity, Contact, Profile, Usage, Marketing Communications, Technical.</td>
            <td>Legitimate interest (understanding customer usage of products/services, business development, marketing strategy).</td>
          </tr>
          <tr>
            <td>Using data analytics to enhance our website, products/services, marketing, customer relationships, and experiences.</td>
            <td>Technical, Usage.</td>
            <td>Legitimate interest (identifying customer types, keeping our website relevant, business development, marketing strategy).</td>
          </tr>
          <tr>
            <td>Suggesting and recommending services to you that may be of your interest.</td>
            <td>Identity, Contact, Technical, Usage, Profile, Marketing Communications.</td>
            <td>Legitimate interest (product/service development, business growth).</td>
          </tr>
          <tr>
            <td>Monitoring our communications with you for the purposes of checking instructions, training, crime prevention, improving customer service, and defending legal claims.</td>
            <td>Identity, Contact, Technical.</td>
            <td>Legitimate interest (employee training, business protection), Legal obligations.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>We will only use your personal data for the purposes for which we collected it, unless we reasonably consider that we need to use it for another reason and that reason is compatible with the original purpose. If we need to use your personal data for an unrelated purpose, we will notify you and we will explain the legal basis which allows us to do so.</p>

    <h4>Security</h4>
    <p>We use appropriate technical and organizational measures to protect the personal information that we collect and process about you.</p>

    <h4>Changes to This Privacy Policy</h4>
    <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.</p>

    <h4>Contact Us</h4>
    <p>If you have any questions about this Privacy Policy, please contact us by email at <a href="mailto:contact@nuitee.com">contact@nuitee.com</a>.</p>
  </>
);

export default NuiteePrivacyContent;
